import React, { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { ArrowRight, Sparkles } from "lucide-react";
import { Aurora, ParticleField } from "./Background";
import CalendlyButton from "./CalendlyButton";

/**
 * Award-worthy hero:
 * - Masked line-by-line reveal (CSS keyframes for reliability)
 * - Mouse-follow 3D parallax tilt on portrait
 * - Framer stagger for supporting UI
 */
export default function Hero() {
  const tiltRef = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [8, -8]), {
    stiffness: 120,
    damping: 18,
  });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-10, 10]), {
    stiffness: 120,
    damping: 18,
  });
  const shineX = useTransform(mx, [-0.5, 0.5], ["20%", "80%"]);
  const shineY = useTransform(my, [-0.5, 0.5], ["20%", "80%"]);

  const onMouseMove = (e) => {
    const el = tiltRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const onLeave = () => {
    mx.set(0);
    my.set(0);
  };

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-screen pt-32 pb-20 overflow-hidden"
    >
      <Aurora />
      <ParticleField count={26} />

      <div className="relative max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.05 }}
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full glass text-xs md:text-sm text-[#D8CCFF] mb-6"
            data-testid="hero-badge"
          >
            <Sparkles size={14} className="text-[#27C6FF]" />
            Premium Wellness · Science-Backed Nutrition
          </motion.div>

          {/* Masked line reveal — the signature on-load moment */}
          <h1
            className="font-display font-extrabold text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight"
            data-testid="hero-headline"
            aria-label="Consistency Rewires You"
          >
            <span className="reveal-mask">
              <span
                className="gradient-text"
                style={{ animationDelay: "0.1s" }}
              >
                Consistency
              </span>
            </span>
            <span className="reveal-mask">
              <span
                className="text-white text-glow"
                style={{ animationDelay: "0.28s" }}
              >
                Rewires You
              </span>
            </span>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.55 }}
            className="mt-7 max-w-xl text-[#D8CCFF] text-base md:text-lg leading-relaxed font-body"
            data-testid="hero-subhead"
          >
            Transform your body, mind, and lifestyle with science-backed
            nutrition and sustainable wellness — guided by a clinical dietician
            who treats you as a whole human, not a number on a scale.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.75 }}
            className="mt-9 flex flex-col sm:flex-row flex-wrap gap-4"
          >
            <a
              href="#booking"
              data-testid="hero-cta-book"
              className="btn-primary inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full font-heading font-semibold"
            >
              Book Consultation <ArrowRight size={18} />
            </a>
            <CalendlyButton
              variant="ghost"
              testId="hero-cta-schedule"
              label="Schedule on Calendar"
              className="!px-7 !py-3.5"
            />
            <a
              href="#services"
              data-testid="hero-cta-journey"
              className="btn-ghost-glass inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full font-heading font-semibold"
            >
              Start Your Journey
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.95 }}
            className="mt-12 grid grid-cols-3 max-w-md gap-6"
            data-testid="hero-stats"
          >
            <div>
              <div className="font-display text-2xl md:text-3xl font-bold text-white">
                1,200+
              </div>
              <div className="text-xs text-[#CFAEFF] mt-1">
                Transformations
              </div>
            </div>
            <div>
              <div className="font-display text-2xl md:text-3xl font-bold text-white">
                8+ yrs
              </div>
              <div className="text-xs text-[#CFAEFF] mt-1">
                Clinical Experience
              </div>
            </div>
            <div>
              <div className="font-display text-2xl md:text-3xl font-bold text-white">
                4.9
                <span className="text-[#27C6FF]">★</span>
              </div>
              <div className="text-xs text-[#CFAEFF] mt-1">Client Rating</div>
            </div>
          </motion.div>
        </div>

        {/* Parallax 3D hero portrait */}
        <div className="lg:col-span-5 relative parallax-tilt">
          <motion.div
            ref={tiltRef}
            onMouseMove={onMouseMove}
            onMouseLeave={onLeave}
            initial={{ opacity: 0, scale: 0.94, rotateX: -6 }}
            animate={{ opacity: 1, scale: 1, rotateX: 0 }}
            transition={{ duration: 1.1, delay: 0.35, ease: [0.2, 0.8, 0.2, 1] }}
            style={{ rotateX: rx, rotateY: ry, transformPerspective: 1200 }}
            className="relative aspect-[4/5] rounded-[28px] overflow-hidden founder-clip gradient-border"
            data-testid="hero-image-wrap"
          >
            {/* Real photo — untouched, framed */}
            <img
              src="/uploads/pooja_hero_full.jpeg?v=1"
              alt="Dt. Pooja A H, Founder & Head Dietician at DietDotIn"
              className="absolute inset-0 w-full h-full object-cover"
              style={{ objectPosition: "50% 15%" }}
              loading="eager"
            />

            {/* Editorial spotlight + film grain */}
            <div className="founder-spot" />
            <div className="founder-grain" />

            {/* Edge vignette — blends studio white into brand dark without altering photo */}
            <div className="pointer-events-none absolute inset-0 shadow-[inset_0_0_120px_60px_rgba(20,3,38,0.65)]" />
            <div className="pointer-events-none absolute inset-y-0 left-0 w-1/3 bg-gradient-to-r from-[#140326]/70 via-[#140326]/20 to-transparent" />
            <div className="pointer-events-none absolute inset-y-0 right-0 w-1/3 bg-gradient-to-l from-[#140326]/70 via-[#140326]/20 to-transparent" />
            <div className="pointer-events-none absolute inset-x-0 top-0 h-1/3 bg-gradient-to-b from-[#140326]/80 via-[#140326]/25 to-transparent" />

            {/* Mouse-follow shine */}
            <motion.div
              className="pointer-events-none absolute inset-0"
              style={{
                background: useTransform(
                  [shineX, shineY],
                  ([x, y]) =>
                    `radial-gradient(400px 400px at ${x} ${y}, rgba(255,255,255,0.22), transparent 55%)`
                ),
                mixBlendMode: "screen",
              }}
            />

            {/* Bottom fade + brand caption */}
            <div className="absolute inset-x-0 bottom-0 h-2/5 bg-gradient-to-t from-[#140326] via-[#140326]/70 to-transparent pointer-events-none" />
            <div className="absolute bottom-5 left-5 right-5 glass-strong rounded-2xl p-4">
              <div className="flex items-center gap-3">
                <img
                  src="/brand/logo_thumb.png?v=4"
                  alt="DietDotIn logo"
                  className="w-12 h-12 object-contain drop-shadow-[0_0_14px_rgba(122,43,255,0.65)]"
                />
                <div>
                  <div className="text-white font-heading font-semibold text-sm">
                    Personalised. Clinical. Sustainable.
                  </div>
                  <div className="text-[#CFAEFF] text-xs">
                    Built around your bloodwork & lifestyle.
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          <div className="absolute -top-6 -right-6 w-32 h-32 rounded-full bg-[#27C6FF]/30 blur-3xl pulse-glow" />
          <div className="absolute -bottom-8 -left-8 w-40 h-40 rounded-full bg-[#7A2BFF]/30 blur-3xl pulse-glow" />
        </div>
      </div>

      {/* Scroll indicator — subtle craft */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.2, delay: 1.4 }}
        className="pointer-events-none absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2"
        data-testid="hero-scroll-hint"
      >
        <span className="text-[10px] tracking-[0.35em] uppercase text-[#CFAEFF]/70 font-heading">
          Scroll
        </span>
        <span className="relative block w-px h-14 overflow-hidden bg-white/10">
          <motion.span
            animate={{ y: ["-100%", "100%"] }}
            transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
            className="absolute inset-x-0 h-6 bg-gradient-to-b from-transparent via-[#27C6FF] to-transparent"
          />
        </span>
      </motion.div>

      <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-b from-transparent to-[#140326]" />
    </section>
  );
}
